import { io } from 'socket.io-client';
import { API_CONFIG } from '../config/api.js';
class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.eventListeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this._lastToken = null;
    // Booking rooms a caller has asked to be joined to. Socket.IO room
    // membership doesn't survive a reconnect — a fresh socket.id starts in
    // no rooms at all — so without tracking these and rejoining on every
    // 'connect' event (below), any network blip / tab background-throttle /
    // server restart silently stops that booking's driver_location_updated
    // events from ever arriving again, with no error and no way to recover
    // short of a full page reload. This also fixes the narrower race where
    // joinBookingRoom() is called before the very first connection finishes.
    this._wantedBookingRooms = new Set();
  }
  connect(token) {
    if (this.socket && this.isConnected) {
      return;
    }
    // If a socket instance exists but is not connected, reset it to avoid parallel sockets
    if (this.socket && !this.isConnected) {
      try {
        this.socket.disconnect();
      } catch (e) {
        // ignore
      }
      this.socket = null;
    }
    const serverUrl = API_CONFIG.WS_URL;
    this._lastToken = token;
    
    this.socket = io(serverUrl, {
      auth: {
        token: token
      },
      transports: ['websocket', 'polling'],
      timeout: 20000,
      forceNew: true
    });
    this.setupEventHandlers();
  }
  setupEventHandlers() {
    if (!this.socket) return;
    this.socket.on('connect', () => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      // Restore room membership — this is what makes joinBookingRoom() calls
      // survive both the initial-connect race and any later reconnect.
      this._wantedBookingRooms.forEach((bookingId) => {
        this.socket.emit('join_booking', bookingId);
      });
      // Emit custom event for connection
      this.emit('connection_status', { connected: true });
    });
    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      // Emit custom event for disconnection
      this.emit('connection_status', { connected: false, reason });
      // Attempt to reconnect if not manually disconnected
      if (reason !== 'io client disconnect') {
        this.handleReconnect();
      }
    });
    this.socket.on('connect_error', (error) => {
      this.isConnected = false;
      this.emit('connection_error', error);
      this.handleReconnect();
    });
    // Booking-related events
    this.socket.on('booking_created', (data) => this.emit('booking_created', data));
    this.socket.on('booking_status_update', (data) => this.emit('booking_status_update', data));
    this.socket.on('booking_cancelled', (data) => this.emit('booking_cancelled', data));
    this.socket.on('location_update', (data) => this.emit('location_update', data));
    this.socket.on('driver_location_update', (data) => this.emit('driver_location_update', data));
    this.socket.on('driver_location_updated', (data) => {
      this.emit('driver_location_updated', data);
      this.emit('driver_location_update', data);
    });
    this.socket.on('booking_status_change', (data) => this.emit('booking_status_change', data));
    this.socket.on('user_joined', (data) => this.emit('user_joined', data));
    this.socket.on('ride_request', (data) => this.emit('ride_request', data));
    this.socket.on('booking_request_received', (data) => this.emit('booking_request_received', data));
    this.socket.on('driver_assigned_by_fleet', (data) => this.emit('driver_assigned_by_fleet', data));
    this.socket.on('booking_accepted', (data) => this.emit('booking_accepted', data));
    this.socket.on('booking_declined', (data) => this.emit('booking_declined', data));
    this.socket.on('driver_response_timeout', (data) => this.emit('driver_response_timeout', data));
    this.socket.on('booking_cancelled_by_user', (data) => this.emit('booking_cancelled_by_user', data));
  }
  handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.emit('connection_failed', { 
        message: 'Unable to reconnect to server',
        attempts: this.reconnectAttempts 
      });
      return;
    }
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    setTimeout(() => {
      if (!this.isConnected) {
        // Prefer the last known token (from current session), fallback to storage
        this.connect(this._lastToken || this.getToken());
      }
    }, delay);
  }
  getToken() {
    return localStorage.getItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN);
  }
  // Join a booking room to receive updates. Recorded in _wantedBookingRooms
  // regardless of current connection state — if the socket isn't connected
  // yet (or drops later), the 'connect' handler above rejoins it as soon as
  // a connection exists, instead of this silently doing nothing.
  joinBookingRoom(bookingId) {
    this._wantedBookingRooms.add(bookingId);
    if (!this.socket || !this.isConnected) return;
    this.socket.emit('join_booking', bookingId);
  }
  // Leave a booking room
  leaveBookingRoom(bookingId) {
    this._wantedBookingRooms.delete(bookingId);
    if (!this.socket || !this.isConnected) return;
    this.socket.emit('leave_booking', bookingId);
  }
  // Send location update (for drivers)
  sendLocationUpdate(data) {
    if (!this.socket || !this.isConnected) return;
    this.socket.emit('location_update', data);
  }
  // Send booking status update
  sendBookingStatusUpdate(data) {
    if (!this.socket || !this.isConnected) return;
    this.socket.emit('booking_status_update', data);
  }
  // Driver-specific methods
  joinDriverRoom() {
    if (!this.socket || !this.isConnected) return;
    this.socket.emit('join_driver_room');
  }
  leaveDriverRoom() {
    if (!this.socket || !this.isConnected) return;
    this.socket.emit('leave_driver_room');
  }
  // Send driver response to booking request
  sendDriverResponse(bookingId, response, reason = null) {
    if (!this.socket || !this.isConnected) return;
    this.socket.emit('driver_response', {
      bookingId,
      response,
      reason,
      timestamp: new Date().toISOString()
    });
  }
  // Event listener management
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push(callback);
  }
  off(event, callback) {
    if (!this.eventListeners.has(event)) return;
    const listeners = this.eventListeners.get(event);
    const index = listeners.indexOf(callback);
    if (index > -1) {
      listeners.splice(index, 1);
    }
  }
  emit(event, data) {
    if (!this.eventListeners.has(event)) return;
    const listeners = this.eventListeners.get(event);
    listeners.forEach(callback => {
      try {
        callback(data);
      } catch (_err) {
        // Swallow listener errors to avoid breaking other listeners
      }
    });
  }
  // Get connection status
  getConnectionStatus() {
    return {
      connected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      socketId: this.socket?.id
    };
  }
  // Disconnect WebSocket
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.eventListeners.clear();
      // A deliberate full teardown (e.g. logout) — unlike reconnect(), which
      // should keep and restore room membership, this shouldn't carry stale
      // booking-room intent into whatever session connects next.
      this._wantedBookingRooms.clear();
    }
  }
  // Reconnect WebSocket
  reconnect() {
    if (this.socket) {
      this.socket.disconnect();
    }
    const token = this.getToken();
    if (token) {
      this.connect(token);
    }
  }
}
// Create singleton instance
const webSocketService = new WebSocketService();
export default webSocketService;
